import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { ECommerceOrderType, ECommercePaymentTransactionsStatus } from '@hooks-api/types/dataModel/eCommercePayments'
import { create } from 'zustand'
import * as paymentTransactionService from '../../core/paymentTransactionService'
import { cartStoreMap, type PlatformType } from './store-map'
import { useFpB2bCartCalculate } from './use-fp-b2b-cart-calculate'

const usePaymentServiceStore = create<{
  payMePaymentQRCodeUrl: string | null
  setPayMePaymentQRCodeUrl: (payMePaymentQRCodeUrl: string | null) => void
}>()(set => ({
  payMePaymentQRCodeUrl: null,
  setPayMePaymentQRCodeUrl: (payMePaymentQRCodeUrl: string | null) => set({ payMePaymentQRCodeUrl }),
}))

interface UseFpB2bPaymentServiceArgs {
  campaignId: number
  campaignCode?: string
  paymentTransactionIdFromUrl?: string
  platform: PlatformType
}

export const useFpB2bPaymentService = (args: UseFpB2bPaymentServiceArgs) => {
  const { campaignId, campaignCode, paymentTransactionIdFromUrl, platform } = args
  const { useCartStore, cartDefaultValues } = cartStoreMap[platform]
  const [cartValues, setCartValues] = useCartStore(campaignId)
  const { calculateResult } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const payMePaymentQRCodeUrl = usePaymentServiceStore(state => state.payMePaymentQRCodeUrl)
  const setPayMePaymentQRCodeUrl = usePaymentServiceStore(state => state.setPayMePaymentQRCodeUrl)

  /**
   * 設定訂單付款交易 id
   */
  const setPaymentTransactionId = useCallbackEvent((paymentTransactionId: string) => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: paymentTransactionService.setPaymentTransactionId(state.paymentTransaction, paymentTransactionId),
    }))
  })

  const setPaymentTransactionStatus = useCallbackEvent((paymentTransactionStatus: ECommercePaymentTransactionsStatus) => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: paymentTransactionService.setPaymentTransactionStatus(state.paymentTransaction, paymentTransactionStatus),
    }))
  })

  /**
   * 情境：使用者從第三方頁面付款頁面，直接點擊上一頁回到結帳頁
   * 從 cart store 檢查前端保存的 paymentTransaction 是否為付款中狀態
   * 1. 如果為付款中狀態，則詢問是否要跳轉至重新付款頁面
   * 2. 如果已經是重新付款頁面，不需要再跳轉
   * Note: 僅用 useEffectOnce 執行一次，避免結帳過程中觸發判斷
   */
  const checkIfNeedRedirectToOrderPayPage = useCallbackEvent((callback: () => void) => {
    const isInRetryPaymentPage = window.location.pathname.includes('/retry-payment')
    if (
      isInRetryPaymentPage
      || !cartValues.paymentTransaction.id
      || cartValues.paymentTransaction.status !== ECommercePaymentTransactionsStatus.PENDING
    ) return
    callback()
  })

  const getPaymentOrder = () => {
    if (paymentTransactionIdFromUrl) return { order_type: ECommerceOrderType.TRANSACTION, order_id: paymentTransactionIdFromUrl }
    if (calculateResult?.cart_uuid) return { order_type: ECommerceOrderType.CART, order_id: calculateResult.cart_uuid }
    return {}
  }

  const { order_type: orderType, order_id: orderId } = getPaymentOrder()

  const resetPaymentTransaction = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: cartDefaultValues.paymentTransaction,
    }))
  })

  const { id: paymentTransactionId, ...restPaymentTransaction } = cartValues.paymentTransaction

  return {
    ...restPaymentTransaction,
    paymentTransactionId,
    checkIfNeedRedirectToOrderPayPage,
    setPaymentTransactionId,
    setPaymentTransactionStatus,
    resetPaymentTransaction,
    orderId,
    orderType,
    payMePaymentQRCodeUrl,
    setPayMePaymentQRCodeUrl,
  }
}
